import React, { FormEvent, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import AddressFields from 'components/address/AddressFields';
import InsideSaving from 'components/loading/InsideSaving';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { AddressErrors, emptyAddress, isAddressFilled, validateAddress } from 'helpers/addressValidation';
import { api } from 'services/api';
import history from 'services/history';
import { Bank } from 'types/bank';
import { Supplier, SupplierAddress, SupplierBank, SupplierProduct } from 'types/supplier';
import SupplierBanks from '../SupplierBanks';
import SupplierProducts from '../SupplierProducts';
import SupplierForm from '../form/SupplierForm';
import SupplierTab from '../tab/SupplierTab';
import { useSupplierValidation } from '../validation/useSupplierValidation';

const NewSupplier: React.FC = () => {
  const [validation, setValidation, validate] = useSupplierValidation();
  const [tab, setTab] = useState('main');
  const [saving, setSaving] = useState(false);
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
  const [banks, setBanks] = useState<Bank[]>([]);
  const [supplierAddress, setSupplierAddress] = useState<SupplierAddress>({ ...emptyAddress, supplier_id: 0 });
  const [supplier, setSupplier] = useState<Supplier>({
    id: 0,
    email: '',
    company_name: '',
    company_trade_name: '',
    phone: '',
    document_number: '',
    active: true,
    address: null,
    banks: [],
    person_type: 'N',
    products: [],
    category: '',
  });

  useEffect(() => {
    fetch('https://brasilapi.com.br/api/banks/v1')
      .then(response => response.json())
      .then(data => setBanks(data.filter((item: Bank) => !!item.code)))
      .catch(error => console.error('Error fetching bank data:', error));
  }, []);

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    if (Object.keys(validation).length > 0) setTab('main');
  }, [validation]);

  function goBack() {
    history.push('/suppliers');
  }

  function handleChange<K extends keyof Supplier>(index: K, value: Supplier[K]) {
    setSupplier(prev => ({ ...prev, [index]: value }));
  }

  function handleAddressChange(address: SupplierAddress) {
    setSupplierAddress(address);
    setAddressErrors({});
  }

  function handleSubmit() {
    setSaving(true);

    const data = {
      ...supplier,
      address: isAddressFilled(supplierAddress)
        ? {
            zip_code: supplierAddress.zip_code,
            street: supplierAddress.street,
            neighborhood: supplierAddress.neighborhood,
            city: supplierAddress.city,
            state: supplierAddress.state,
            street_number: supplierAddress.street_number,
            complement: supplierAddress.complement,
          }
        : null,
    };

    api
      .post('/suppliers', data)
      .then(() => {
        toast.success('Fornecedor cadastrado');
        history.push('/suppliers');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível cadastrar o fornecedor');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();

    validate(supplier)
      .then(() => {
        const errors = validateAddress(supplierAddress);
        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
          setTab('address');
          toast.error('Corrija o endereço para salvar');
          return;
        }

        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  // produtos e contas ficam na memória até o fornecedor ser salvo
  function handleChangeAddBank(bank: SupplierBank) {
    setSupplier(prev => {
      const existingBank = prev.banks.find(b => b.id === bank.id);
      if (existingBank) {
        return {
          ...prev,
          banks: prev.banks.map(b => (b.id === bank.id ? { ...b, ...bank } : b)),
        };
      }
      return {
        ...prev,
        banks: [...prev.banks, { ...bank, id: prev.banks.length ? Math.max(...prev.banks.map(b => b.id)) + 1 : 1 }],
      };
    });
  }

  function handleDeleteBank(bank: SupplierBank) {
    setSupplier(prev => ({
      ...prev,
      banks: prev.banks.filter(b => b.id !== bank.id),
    }));
  }

  function handleChangeAddProduct(product: SupplierProduct) {
    setSupplier(prev => {
      const existingProduct = prev.products.find(b => b.id === product.id);
      if (existingProduct) {
        return {
          ...prev,
          products: prev.products.map(b => (b.id === product.id ? { ...b, ...product } : b)),
        };
      }
      return {
        ...prev,
        products: [
          ...prev.products,
          { ...product, id: prev.products.length ? Math.max(...prev.products.map(b => b.id)) + 1 : 1 },
        ],
      };
    });
  }

  function handleDeleteProduct(product: SupplierProduct) {
    setSupplier(prev => ({
      ...prev,
      products: prev.products.filter(b => b.id !== product.id),
    }));
  }

  return (
    <>
      <Appbar title="Fornecedores" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title="Novo fornecedor" backAction={goBack} />

        <WorkArea>
          <SupplierTab
            onChange={newTab => setTab(newTab)}
            tab={tab}
            productsCount={supplier.products.length}
            banksCount={supplier.banks.length}
          />
          <ContentPad>
            {tab === 'products' ? (
              <SupplierProducts
                supplier={supplier}
                handleDeleteProduct={handleDeleteProduct}
                handleChangeAddProduct={handleChangeAddProduct}
              />
            ) : tab === 'banks' ? (
              <SupplierBanks
                handleDeleteBank={handleDeleteBank}
                banks={banks}
                supplier={supplier}
                handleChangeAddBank={handleChangeAddBank}
              />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'address' ? (
                  <AddressFields address={supplierAddress} onChange={handleAddressChange} errors={addressErrors} />
                ) : (
                  <SupplierForm handleChange={handleChange} supplier={supplier} validation={validation} />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        <RecordFooter>
          <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
            Salvar fornecedor
          </Button>
        </RecordFooter>
      </PageShell>
    </>
  );
};

export default NewSupplier;
